import { useState, useRef, useCallback } from 'react';
import { Upload, FileSpreadsheet, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

export interface BulkUploadColumn {
  key: string;
  label: string;
  required?: boolean;
  type?: 'string' | 'number' | 'boolean' | 'date';
  validate?: (value: string) => string | null; // Returns error message or null
}

export interface ParsedRow {
  rowNumber: number;
  data: Record<string, string>;
  errors: string[];
  isValid: boolean;
}

export interface BulkUploadProps {
  columns: BulkUploadColumn[];
  onUpload: (validRows: Record<string, string>[]) => Promise<{ success: number; failed: number }>;
  templateFileName?: string;
  maxRows?: number;
}

function parseCSV(content: string): string[][] {
  const lines = content.split(/\r?\n/).filter((line) => line.trim());
  return lines.map((line) => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  });
}

export function BulkUpload({
  columns,
  onUpload,
  templateFileName = 'template.csv',
  maxRows = 1000,
}: BulkUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ success: number; failed: number } | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateRow = useCallback(
    (rowData: Record<string, string>, rowNumber: number): ParsedRow => {
      const errors: string[] = [];

      // Check required fields
      columns.forEach((col) => {
        if (col.required && !rowData[col.key]?.trim()) {
          errors.push(`${col.label} is required`);
        }
      });

      // Type validation
      columns.forEach((col) => {
        const value = rowData[col.key]?.trim();
        if (!value) return;

        if (col.type === 'number' && isNaN(Number(value))) {
          errors.push(`${col.label} must be a number`);
        }
        if (
          col.type === 'boolean' &&
          !['true', 'false', '1', '0', 'yes', 'no'].includes(value.toLowerCase())
        ) {
          errors.push(`${col.label} must be true/false`);
        }
        if (col.type === 'date' && isNaN(Date.parse(value))) {
          errors.push(`${col.label} must be a valid date`);
        }

        // Custom validation
        if (col.validate) {
          const customError = col.validate(value);
          if (customError) errors.push(customError);
        }
      });

      return {
        rowNumber,
        data: rowData,
        errors,
        isValid: errors.length === 0,
      };
    },
    [columns]
  );

  const processFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const rows = parseCSV(content);

        if (rows.length < 2) {
          setParsedData([
            {
              rowNumber: 0,
              data: {},
              errors: ['File is empty or has no data rows'],
              isValid: false,
            },
          ]);
          return;
        }

        const headers = rows[0].map((h) => h.toLowerCase().replace(/\s+/g, '_'));
        const dataRows = rows.slice(1, maxRows + 1);

        const parsed: ParsedRow[] = dataRows.map((row, index) => {
          const rowData: Record<string, string> = {};
          headers.forEach((header, i) => {
            // Map header to column key
            const column = columns.find(
              (c) =>
                c.key.toLowerCase() === header ||
                c.label.toLowerCase().replace(/\s+/g, '_') === header
            );
            if (column) {
              rowData[column.key] = row[i] || '';
            }
          });
          return validateRow(rowData, index + 2);
        });

        setParsedData(parsed);
        setUploadResult(null);
      };
      reader.readAsText(file);
    },
    [columns, maxRows, validateRow]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      processFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const downloadTemplate = () => {
    const headers = columns.map((c) => c.label).join(',');
    const sampleRow = columns
      .map((c) => {
        if (c.type === 'number') return '0';
        if (c.type === 'boolean') return 'true';
        if (c.type === 'date') return new Date().toISOString().split('T')[0];
        return `Sample ${c.label}`;
      })
      .join(',');

    const csv = `${headers}\n${sampleRow}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = templateFileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    const validRows = parsedData.filter((r) => r.isValid).map((r) => r.data);
    if (validRows.length === 0) return;

    setIsUploading(true);
    try {
      const result = await onUpload(validRows);
      setUploadResult(result);
    } catch {
      setUploadResult({ success: 0, failed: validRows.length });
    } finally {
      setIsUploading(false);
    }
  };

  const clearData = () => {
    setParsedData([]);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = parsedData.filter((r) => r.isValid).length;
  const invalidCount = parsedData.filter((r) => !r.isValid).length;

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      {parsedData.length === 0 && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm text-status-neutral mb-2">Drag and drop a CSV file here, or</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          <div className="flex gap-2 justify-center">
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Browse Files
            </Button>
            <Button variant="ghost" size="sm" onClick={downloadTemplate}>
              Download Template
            </Button>
          </div>
        </div>
      )}

      {/* Parsed Data Preview */}
      {parsedData.length > 0 && (
        <Card>
          <CardContent className="p-4">
            {/* Summary */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-status-success" />
                  <span className="text-sm">{validCount} valid rows</span>
                </div>
                {invalidCount > 0 && (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-status-destructive" />
                    <span className="text-sm text-status-destructive">
                      {invalidCount} rows with errors
                    </span>
                  </div>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={clearData}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            </div>

            {/* Data Table */}
            <ScrollArea className="h-[300px] border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">Row</TableHead>
                    <TableHead className="w-[80px]">Status</TableHead>
                    {columns.map((col) => (
                      <TableHead key={col.key}>
                        {col.label}
                        {col.required && <span className="text-status-destructive">*</span>}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedData.map((row) => (
                    <TableRow
                      key={row.rowNumber}
                      className={row.isValid ? '' : 'bg-status-destructive-subtle'}
                    >
                      <TableCell className="font-mono text-xs">{row.rowNumber}</TableCell>
                      <TableCell>
                        {row.isValid ? (
                          <Badge
                            variant="outline"
                            className="text-status-success border-status-success-border"
                          >
                            Valid
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-status-destructive border-status-destructive-border"
                          >
                            Error
                          </Badge>
                        )}
                      </TableCell>
                      {columns.map((col) => (
                        <TableCell key={col.key} className="max-w-[150px] truncate">
                          {row.data[col.key] || '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Error Details */}
            {invalidCount > 0 && (
              <div className="mt-4 p-3 bg-status-destructive-subtle rounded-md">
                <p className="text-sm font-medium text-status-destructive mb-2">Errors found:</p>
                <ul className="text-xs text-status-destructive space-y-1">
                  {parsedData
                    .filter((r) => !r.isValid)
                    .slice(0, 5)
                    .map((r) => (
                      <li key={r.rowNumber}>
                        Row {r.rowNumber}: {r.errors.join(', ')}
                      </li>
                    ))}
                  {invalidCount > 5 && <li>... and {invalidCount - 5} more errors</li>}
                </ul>
              </div>
            )}

            {/* Upload Result */}
            {uploadResult && (
              <div
                className={`mt-4 p-3 rounded-md ${
                  uploadResult.failed === 0
                    ? 'bg-status-success-subtle'
                    : 'bg-status-warning-subtle'
                }`}
              >
                <p className="text-sm">
                  <span className="font-medium text-status-success">
                    {uploadResult.success} rows uploaded successfully
                  </span>
                  {uploadResult.failed > 0 && (
                    <span className="text-status-destructive ml-2">
                      ({uploadResult.failed} failed)
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 mt-4 justify-end">
              <Button variant="outline" onClick={clearData}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={validCount === 0 || isUploading}>
                {isUploading ? 'Uploading...' : `Upload ${validCount} Rows`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default BulkUpload;
